import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useCertificates from 'PlatForms/SelfLMS/Hooks/Certificates/useCertificate';
import {
  FaCertificate,
  FaAward,
  FaArrowLeft
} from 'react-icons/fa';
import { Card, Button, Typography, Space, Alert, message, Pagination } from 'antd';
import badgeImage from "Common/hooks/assets/images/badge.jpg";
import { ProfileContext } from 'Common/hooks/context/ProfileContext';

const { Title, Text } = Typography;

const CredentialsPage = () => {
  const [activeTab, setActiveTab] = useState('certificates');
  const navigate = useNavigate();
 const {
  certificates,
  loading,
  error,
  pageNumber,
  pageSize,
  totalCertificates,
  setPageNumber,
  setPageSize,
  refetch
} = useCertificates();

  const { isProfileComplete } = useContext(ProfileContext);
  const isBlocked = !isProfileComplete;

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);


  // Reset to first page when certificates change
  useEffect(() => {
    setCurrentPage(1);
  }, [certificates]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleCardClick = (certificate) => {
    if (isBlocked) {
      message.warning("Please complete your profile first");
      return;
    }

    navigate(`/selfLearner-dashboard/certificate/${certificate.id}`, {
      state: { certificate }
    });
  };

  const handleBack = () => {
    navigate("/selfLearner-dashboard");
  };

  // Handle pagination change
  const handlePageChange = (page, size) => {
    setCurrentPage(page);
    if (size !== pageSize) {
      setPageSize(size);
      setCurrentPage(1); // Reset to first page when page size changes
    }
  };

  // Get current page data
  const getCurrentPageData = () => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return certificates.slice(startIndex, endIndex);
  };

  // Loading Skeleton
  const CertificateSkeleton = () => (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px', marginTop: '12px' }}>
      {[...Array(6)].map((_, index) => (
        <Card key={index} className="card-professional" style={{ padding: '16px' }}>
          <div style={{ height: '24px', background: '#EFEADF', marginBottom: '16px', borderRadius: '3px' }}></div>
          <div style={{ width: '40%', height: '20px', background: '#EFEADF', marginBottom: '20px', borderRadius: '3px' }}></div>
          <div style={{ height: '14px', background: '#EFEADF', marginBottom: '8px', borderRadius: '3px' }}></div>
          <div style={{ height: '14px', width: '70%', background: '#EFEADF', marginBottom: '8px', borderRadius: '3px' }}></div>
          <div style={{ height: '14px', width: '60%', background: '#EFEADF', marginBottom: '20px', borderRadius: '3px' }}></div>
          <div style={{ height: '20px', width: '30%', background: '#EFEADF', borderRadius: '3px' }}></div>
        </Card>
      ))}
    </div>
  );

  // Get current page data for rendering
  const currentCertificates = getCurrentPageData();

  return (
    <div style={{ minHeight: '100vh', backgroundColor: 'transparent', padding: '20px' }}>
      {/* Show incomplete profile alert if profile is not complete */}
      {isBlocked && (
        <Alert
          message="Complete Your Profile"
          description="Please complete your profile to access credentials."
          type="warning"
          showIcon
          style={{ marginBottom: '20px' }}
          action={
            <Button
              size="small"
              type="primary"
              onClick={() => navigate("/selfLearner-profile")}
            >
              Complete Profile
            </Button>
          }
          closable
        />
      )}

      {/* Header Card */}
      <Card
        title={
          <Space>
            <FaAward style={{ color: '#9A6B0E' }} />
            Credentials
          </Space>
        }
        className="card-professional"
        extra={
          <Button type="default" icon={<FaArrowLeft />} onClick={handleBack}>
            Back
          </Button>
        }
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <Text type="secondary">Manage and view your professional achievements</Text>

          {/* Tabs */}
          <div style={{ display: 'flex', backgroundColor: '#F6F3EC', borderRadius: '6px', padding: '3px', width: 'fit-content' }}>
            <Button
              type={activeTab === 'certificates' ? 'primary' : 'text'}
              icon={<FaCertificate />}
              onClick={() => setActiveTab('certificates')}
              style={{
                marginRight: '4px',
                borderRadius: '4px'
              }}
            >
              Badges
            </Button>

          </div>

          {/* Error State */}
          {error && !loading && (
            <Alert
              message="Error Loading Certificates"
              description={error}
              type="error"
              showIcon
              action={
                <Space>
                  <Button size="small" onClick={refetch}>
                    Try Again
                  </Button>
                  <Button size="small" onClick={() => window.location.reload()}>
                    Reload Page
                  </Button>
                </Space>
              }
            />
          )}

          {/* Certificates Tab Content */}
          {activeTab === 'certificates' && !error && (
            <>
              {loading ? (
                <CertificateSkeleton />
              ) : certificates.length === 0 ? (
                <Card className="card-professional" style={{ textAlign: 'center', padding: '40px' }}>
                  <img
                    src={badgeImage}
                    alt="No Badges"
                    style={{
                      width: '60px',
                      height: '60px',
                      borderRadius: '8px',
                      marginBottom: '16px',
                      opacity: 0.5
                    }}
                  />
                  <Title level={4} style={{ color: '#57534E', marginBottom: '8px' }}>
                    No Badges Yet
                  </Title>
                  <Text style={{ color: '#57534E', fontSize: '14px' }}>
                    Complete courses to earn badges
                  </Text>
                </Card>
              ) : (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px', marginTop: '16px' }}>
                    {currentCertificates.map((certificate) => (
                      <Card
                        key={certificate.id}
                        className={`card-professional ${isBlocked ? '' : 'clickable-card'}`}
                        hoverable={!isBlocked}
                        onClick={() => handleCardClick(certificate)}
                        bodyStyle={{ padding: '16px' }}
                        style={{
                          borderRadius: '10px',
                          height: '100%',
                          cursor: isBlocked ? 'not-allowed' : 'pointer'
                        }}
                      >
                        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                          {/* Top Row: Badge Image and Title */}
                          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '12px' }}>
                            <div style={{
                              width: '70px',
                              height: '70px',
                              borderRadius: '10px',
                              overflow: 'hidden',
                              flexShrink: 0,
                              border: '2px solid #E7E1D5'
                            }}>
                              <img
                                src={badgeImage}
                                alt="Badge"
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover'
                                }}
                              />
                            </div>

                            <div style={{ flex: 1, minWidth: 0 }}>
                              {/* Title with ellipsis */}
                              <Text strong style={{
                                fontSize: '16px',
                                color: '#1C1917',
                                display: 'block',
                                marginBottom: '6px',
                                lineHeight: '1.3',
                                fontWeight: '600'
                              }}>
                                {certificate.title}
                              </Text>

                              {/* Status Badge */}
                              <span style={{
                                padding: '3px 8px',
                                borderRadius: '6px',
                                fontSize: '11px',
                                fontWeight: '600',
                                backgroundColor: certificate.isRevoked ? '#fee2e2' : '#F6EFDD',
                                color: certificate.isRevoked ? '#991b1b' : '#9A6B0E',
                                border: certificate.isRevoked ? '1px solid #fecaca' : '1px solid #E7D3A8'
                              }}>
                                {certificate.isRevoked ? 'Revoked' : 'Verified'}
                              </span>
                            </div>
                          </div>

                          {/* Middle Row: Details */}
                          <div style={{ marginBottom: '12px', flex: 1 }}>
                            {/* Issue Date */}
                            <div style={{ marginBottom: '8px' }}>
                              <Text style={{
                                fontSize: '12px',
                                color: '#57534E',
                                fontWeight: '500',
                                marginBottom: '2px',
                                marginRight: '6px'
                              }}>
                                Issued Date:
                              </Text>
                              <Text style={{
                                fontSize: '14px',
                                color: '#57534E',
                                fontWeight: '600'
                              }}>
                                {formatDate(certificate.issuedOn)}
                              </Text>
                            </div>

                          </div>

                          {/* Bottom Row: Action */}
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            paddingTop: '12px',
                            borderTop: '1px solid #E7E1D5',
                            marginTop: 'auto'
                          }}>
                            <Text style={{
                              fontSize: '13px',
                              color: '#1C1917',
                              fontWeight: '600'
                            }}>
                              View Details
                            </Text>
                            <Text style={{
                              fontSize: '14px',
                              color: '#1C1917',
                              fontWeight: 'bold'
                            }}>
                              →
                            </Text>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>

                  {/* Pagination Component */}
          
  <div style={{ display: 'flex', justifyContent: 'center', marginTop: '24px' }}>
    <Pagination
      current={pageNumber}
      pageSize={pageSize}
      total={totalCertificates}
      onChange={(page, size) => {
        setPageNumber(page);
        setPageSize(size);
      }}
      showSizeChanger
      showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} badges`}
      pageSizeOptions={['6', '9', '12', '18', '24']}
    />
  </div>


                </>
              )}
            </>
          )}
        </Space>
      </Card>
    </div>
  );
};

export default CredentialsPage;